import { defineStore, StoreDefinition } from 'pinia';
import { type ComputedRef } from '@vue/composition-api';
import { ref } from '@vue/composition-api';
import { computed } from 'vue-demi';
import { useComputed } from 'common-module/src/util/useComputed';

export type Feature = boolean | string | undefined;
export type Features = Record<string, Feature>;
type SwitchStoreDefinition = StoreDefinition<string, {}, {
  features: ComputedRef<Features>;
}, {
  setFeatures(feature: Features): void;
  setFeature(feature: string, value: Feature): void;
  isSwitch(names?: string[], every?: boolean): boolean;
  isSwitchComputed(names?: string[], every?: boolean): ComputedRef<boolean>;
  getSwitchValue(feature: string): Feature;
  getSwitchValueComputed(feature: string): ComputedRef<Feature>;
  refreshFeatures<Args extends unknown[]>(...args: Args): Promise<void>;
}>
export type SwitchStoreOptions = {
  load?: (...args: any[]) => Promise<Features> | Features;
  dependencies?: SwitchStoreDefinition[];
}

export const isSwitchOn = (getSwitchValue: (feature: string) => Feature, names?: string[], every?: boolean) => {
  if (!names?.length) {
    return true;
  }

  const values = names.map(name => getSwitchValue(name));
  if (every) {
    return values.every(value => !!value);
  }

  return values.some(value => !!value);
};

export const createSwitchStore = (name: string, options?: SwitchStoreOptions): SwitchStoreDefinition => {
  const { load, dependencies: _dependencies = [] } = options || {};
  return defineStore(name, () => {
    const dependencies = _dependencies.map(dep => dep());
    const selfFeatures = ref<Features>({});

    const features = computed(() => ({
      ...selfFeatures.value,
      ...dependencies.reduce((acc, dep) => ({ ...acc, ...dep.features }), {} as Features)
    }));

    const setFeatures = (newFeatures: Features) => {
      selfFeatures.value = newFeatures;
    };
    const setFeature = (feature: string, value: Feature) => {
      selfFeatures.value[feature] = value;
    };
    const getSwitchValue = (feature: string) => features.value[feature];
    const isSwitch = (names?: string[], every?: boolean) => {
      return isSwitchOn(getSwitchValue, names, every);
    };

    const isSwitchComputed = useComputed(isSwitch);
    const getSwitchValueComputed = useComputed(getSwitchValue);

    const refreshFeatures = async <Args extends unknown[]>(...args: Args) => {
      const newFeatures = await (load || (() => Promise.resolve({})))(...args);
      setFeatures(newFeatures);
    };

    return {
      features,
      setFeatures,
      setFeature,
      isSwitch,
      isSwitchComputed,
      getSwitchValue,
      getSwitchValueComputed,
      refreshFeatures
    };
  });
};
